/**
 * Маппинг вложений `FILEATTACHMENTS` между `UniDataTable` платформы и `FileItem[]` UI.
 *
 * Используется для: fill/refill в store, исходящий `getData` / `getFullData` по WebSocket,
 * `POST /platform/upload` (`currentData`, разбор ответа), merge после upload и `reloadElement`.
 *
 * @see dev/WUI-293.md — п. 7–11, 15–17
 */
import type { FileItem, FileData } from '@/common/types/fileItem'
import type { R7DocEditorInitConfig } from '@/common/types/r7DocEditorInitConfig'
import { IKey } from '@/common/constants/ikey'
import { previewBytesFromFields } from '@/common/utils/attachmentPreviewUtils'
import { FILE_ATTACHMENT_ATTRIBUTE_KEYS } from '@/common/utils/attachmentRpcPolicy'
import { isMiddleErrorResponse } from '@/common/utils/middleErrorResponse'
import UniData from '@/common/classes/UniData'
import { normalizeFormElementType } from '@/common/utils/formElementType'
import { isFieldUniDataTable } from '@/common/utils/jsonUniData'

/** Ключи `storage_uri` в row / currentData (канон + legacy). */
export const ATTACHMENT_STORAGE_URI_FIELD_KEYS = [
  IKey.STORAGE_URI,
  'storageUri',
  '__storage_uri',
  '__storageUri',
] as const

/** Служебный ключ строки: только первый prepare локальной строки (R11); не уходит в UniData RPC. */
export const ATTACHMENT_CLIENT_ROW_KEY = '__clientRowKey' as const

/** Служебный ключ на строке web (не уходит в UniData RPC ленты). */
export const ATTACHMENT_DOCUMENT_SESSION_ID = '__documentSessionId' as const

/** Имя поля в `currentData` для middleware (`OfficeEditorService.DOCUMENT_SESSION_ID`). */
export const MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD = 'documentSessionId' as const

function setMiddlewareDocumentSessionIdField(
  fieldMap: Record<string, TypedPlatformField>,
  documentSessionId: string,
): void {
  const id = String(documentSessionId ?? '').trim()
  if (!id) return
  fieldMap[MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD] = toTypedField(id, 'String')
}

export function getAttachmentClientRowKey(fields: Record<string, unknown>): string | null {
  const raw = getScalarFromFields(fields, ATTACHMENT_CLIENT_ROW_KEY, 'clientRowKey')
  const key = String(raw ?? '').trim()
  return key || null
}

export function withAttachmentClientRowKey(
  fields: FileData['fields'],
  clientRowKey: string,
): FileData['fields'] {
  const key = String(clientRowKey ?? '').trim()
  if (!key) return fields
  return {
    ...fields,
    [ATTACHMENT_CLIENT_ROW_KEY]: toTypedField(key, 'String'),
  }
}

/** Скопировать `__clientRowKey` с подготовленной строки на актуальную строку store (после modal prepare). */
export function alignAttachmentClientRowKey(source: FileItem, target: FileItem): FileItem {
  const sourceKey = getAttachmentClientRowKey((source.fields ?? {}) as Record<string, unknown>)
  if (!sourceKey) return target
  const targetKey = getAttachmentClientRowKey((target.fields ?? {}) as Record<string, unknown>)
  if (targetKey === sourceKey) return target
  return {
    ...target,
    fields: withAttachmentClientRowKey(target.fields ?? {}, sourceKey),
  }
}

/** Гарантирует `clientRowKey` на строке (мутация `item.fields`). */
export function ensureAttachmentClientRowKey(item: FileItem): string {
  const fields = item.fields ?? {}
  const existing = getAttachmentClientRowKey(fields)
  if (existing) return existing
  const key =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `client-${Date.now()}-${Math.random().toString(36).slice(2)}`
  item.fields = withAttachmentClientRowKey(fields, key)
  return key
}

/** Поля вложенных таблиц — pass-through в исходящем getData (п. 17 WUI-293). */
const PASS_THROUGH_TABLE_KEYS = [
  'stamp_registration_list',
  'stamp_signature_list',
  'signature_file_list',
] as const

const PASS_THROUGH_SCALAR_KEYS = ['stampable', 'stampableFile'] as const

/** Исходящие имена directions (как в Swing `IKeys` / дампах). */
export const ATTACHMENT_DIRECTIONS_OUTGOING = [
  { canonical: 'xpathList', legacy: 'directions_xpath_list' },
  { canonical: 'xsltTemplate', legacy: 'directions_xslt_template' },
  { canonical: 'signTemplate', legacy: 'directions_sign_template' },
  { canonical: 'signatureMethod', legacy: 'directions_signature_method' },
] as const

const PLATFORM_TAIL_PREFIXES = ['_changer.', 'filePath', 'sendByMedo', 'sendByEmail', 'templateId']

const MAX_JSON_UNWRAP_DEPTH = 5

/** Поле UniData в wire-формате: `{ type, value }` (как в `changed_data` платформы). */
export type TypedPlatformField = { type: string; value: unknown }

/**
 * Разворачивает вложенные JSON-строки в ответе middleware (`ResponseDocument` и т.п.).
 *
 * @param payload — тело HTTP или уже распарсенный объект
 * @param depth — защита от бесконечной рекурсии при двойной сериализации
 */
export function unwrapPlatformJsonPayload(payload: unknown, depth = 0): unknown {
  if (payload == null || depth >= MAX_JSON_UNWRAP_DEPTH) return payload
  if (typeof payload !== 'string') return payload

  let trimmed = payload.trim()
  if (trimmed.charCodeAt(0) === 0xfeff) {
    trimmed = trimmed.slice(1).trim()
  }
  if (!trimmed || trimmed === 'null') return null

  try {
    const parsed = JSON.parse(trimmed)
    if (typeof parsed === 'string') {
      return unwrapPlatformJsonPayload(parsed, depth + 1)
    }
    return parsed
  } catch {
    return payload
  }
}

/** Типизированная таблица вложений для store и RPC (имя поля — снаружи, напр. `attachments`). */
export type UniDataTableWire = {
  type: 'UniDataTable'
  value: {
    rows: Array<{ fields: Record<string, TypedPlatformField>; errors?: Record<string, unknown>; attributes?: Record<string, unknown> }>
    empty?: boolean
    columns?: string[]
    attributes?: Record<string, unknown>
  }
}

function deepClone<T>(value: T): T {
  if (value === undefined || value === null) return value
  return JSON.parse(JSON.stringify(value)) as T
}

function unwrapFieldValue(field: unknown): unknown {
  if (field == null) return undefined
  if (typeof field === 'object' && field !== null && 'value' in field) {
    return (field as TypedPlatformField).value
  }
  return field
}

function isTypedField(field: unknown): field is TypedPlatformField {
  return (
    field != null &&
    typeof field === 'object' &&
    'type' in field &&
    'value' in field
  )
}

function tableRowCount(field: unknown): number {
  const v = unwrapFieldValue(field)
  if (!v || typeof v !== 'object') return 0
  const rows = (v as { rows?: unknown[] }).rows
  return Array.isArray(rows) ? rows.length : 0
}

function inferScalarType(value: unknown): string {
  if (typeof value === 'boolean') return 'Boolean'
  if (typeof value === 'number' && Number.isInteger(value)) return 'Long'
  if (value instanceof Date) return 'Timestamp'
  return 'String'
}

function toTypedField(value: unknown, typeHint?: string): TypedPlatformField {
  if (isTypedField(value)) {
    return deepClone(value)
  }
  const type = typeHint ?? inferScalarType(value)
  return { type, value }
}

/**
 * Первое непустое скалярное значение из `fields` по списку имён колонок.
 * Поддерживает как typed `{ type, value }`, так и «голое» значение.
 */
export function getScalarFromFields(fields: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    const raw = fields[key]
    const v = unwrapFieldValue(raw)
    if (v !== undefined && v !== null && v !== '') return v
  }
  return undefined
}

/** Извлекает строки UniDataTable из store / fill / Array. */
export function extractAttachmentRows(raw: unknown): Array<Record<string, unknown>> {
  if (raw == null) return []

  if (Array.isArray(raw)) {
    return raw.map((item) => {
      if (item && typeof item === 'object' && 'fields' in item) {
        return (item as { fields: Record<string, unknown> }).fields
      }
      return (item ?? {}) as Record<string, unknown>
    })
  }

  let node: unknown = raw
  if (typeof node === 'object' && node !== null && 'value' in node) {
    node = (node as { value: unknown }).value
  }
  if (typeof node === 'object' && node !== null && 'rows' in node) {
    const rows = (node as { rows: unknown[] }).rows
    if (!Array.isArray(rows)) return []
    return rows.map((row) => {
      if (row && typeof row === 'object' && 'fields' in row) {
        return (row as { fields: Record<string, unknown> }).fields
      }
      return (row ?? {}) as Record<string, unknown>
    })
  }

  return []
}

/** Входящий UniDataTable / Array → `FileItem[]` (глубокая копия `fields`). */
export function uniDataTableToFileItems(raw: unknown): FileItem[] {
  const rowFieldMaps = extractAttachmentRows(raw)
  return rowFieldMaps.map((fields) => ({
    fields: deepClone(fields) as FileItem['fields'],
  }))
}

function resolveFileName(fields: Record<string, unknown>, item?: FileItem): string {
  if (item?.file instanceof File) return item.file.name
  const name = getScalarFromFields(fields, 'name', 'fileName')
  return name != null ? String(name) : ''
}

function resolveSize(fields: Record<string, unknown>, item?: FileItem): number {
  if (item?.file instanceof File) return item.file.size
  const size = getScalarFromFields(fields, 'size', '__size')
  const n = Number(size)
  return Number.isFinite(n) ? n : 0
}

function resolveCode(fields: Record<string, unknown>): number | null {
  const code = getScalarFromFields(fields, 'code', '_id')
  if (code === undefined || code === null || code === '') return null
  const n = Number(code)
  return Number.isFinite(n) && n !== 0 ? n : null
}

function resolveVersion(fields: Record<string, unknown>): number {
  const version = getScalarFromFields(fields, 'version', '_versionId')
  const n = Number(version)
  return Number.isFinite(n) ? n : 0
}

/** Пустой `signature_file_list`, как в Swing `getFullData().toString()` для файла без подписей. */
export function emptySignatureFileListField(): TypedPlatformField {
  return {
    type: 'UniDataTable',
    value: {
      rows: [],
      empty: true,
    },
  }
}

/** Эвристика `pages` как в Swing после `calcPages` (растр → «1», иначе «0»). */
export function inferAttachmentPagesDefault(fileName: string): string {
  const lower = fileName.toLowerCase()
  if (/\.(png|jpe?g|gif|bmp|webp|tiff?|ico)$/i.test(lower)) return '1'
  return '0'
}

const SWING_OUTGOING_ROW_KEYS = [
  'code',
  'name',
  'size',
  'version',
  'signature_file_list',
  'pages',
  'hash',
  'filePath',
  'stamp_registration_list',
  'stamp_signature_list',
  'xpathList',
  'xsltTemplate',
  'signTemplate',
  'signatureMethod',
  'stampable',
  'stampableFile',
] as const

function applyOutgoingDirectionsFields(
  draft: Record<string, TypedPlatformField>,
  src: Record<string, unknown>,
): void {
  for (const { canonical, legacy } of ATTACHMENT_DIRECTIONS_OUTGOING) {
    delete draft[legacy]
    if (draft[canonical] != null) continue
    const field = src[canonical] ?? src[legacy]
    if (canonical === 'xpathList') {
      if (tableRowCount(field) > 0 && isTypedField(field)) {
        draft[canonical] = deepClone(field) as TypedPlatformField
      }
      continue
    }
    if (field == null) continue
    const plain = unwrapFieldValue(field)
    if (plain === undefined || plain === null || plain === '') continue
    draft[canonical] = isTypedField(field) ? deepClone(field) : toTypedField(plain)
  }
}

function orderSwingOutgoingRowFields(
  fields: Record<string, TypedPlatformField>,
): Record<string, TypedPlatformField> {
  const out: Record<string, TypedPlatformField> = {}
  const used = new Set<string>()
  for (const key of SWING_OUTGOING_ROW_KEYS) {
    if (key in fields) {
      out[key] = fields[key]
      used.add(key)
    }
  }
  for (const [key, val] of Object.entries(fields)) {
    if (used.has(key)) continue
    if (key.startsWith('_changer.') || key === 'preview') continue
    out[key] = val
  }
  return out
}

/** Только поле таблицы и `complex__element__name` (без `attachments__action` и пр.). */
export function pruneAttachmentElementRpcFields(
  data: Record<string, unknown>,
  elementName: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (data[elementName] !== undefined) {
    out[elementName] = data[elementName]
  }
  if (data[IKey.COMPLEX_ELEMENT_NAME] !== undefined) {
    out[IKey.COMPLEX_ELEMENT_NAME] = data[IKey.COMPLEX_ELEMENT_NAME]
  }
  return out
}

function attachmentItemsMatch(a: FileItem, b: FileItem): boolean {
  return attachmentItemsReferSameRow(a, b)
}

/** Минимальный набор Swing + pass-through для одной строки. */
export function buildOutgoingRowFields(
  item: FileItem,
  options?: { selected?: boolean },
): Record<string, TypedPlatformField> {
  const src = (item.fields ?? {}) as Record<string, unknown>
  const draft: Record<string, TypedPlatformField> = {}

  const code = resolveCode(src)
  const version = resolveVersion(src)
  const name = resolveFileName(src, item)
  const size = resolveSize(src, item)
  const hash = getScalarFromFields(src, 'hash')
  const pages = getScalarFromFields(src, 'pages')

  if (code != null) {
    draft.code = toTypedField(code, 'Long')
    draft.version = toTypedField(version, 'Long')
  } else if (item?.file instanceof File || getAttachmentClientRowKey(src)) {
    draft.code = toTypedField(-1, 'Long')
    draft.version = toTypedField(-1, 'Long')
  }

  if (name) {
    draft.name = toTypedField(name, 'String')
  }

  if (size > 0 || item?.file) {
    draft.size = toTypedField(size, 'Long')
  }

  if (code != null) {
    const sigField = src.signature_file_list
    if (tableRowCount(sigField) > 0 && isTypedField(sigField)) {
      draft.signature_file_list = deepClone(sigField) as TypedPlatformField
    } else {
      draft.signature_file_list = emptySignatureFileListField()
    }

    const pagesValue =
      pages != null && pages !== '' ? String(pages) : inferAttachmentPagesDefault(name)
    draft.pages = toTypedField(pagesValue, 'String')
  }

  if (hash != null && hash !== '') {
    draft.hash = toTypedField(String(hash), 'String')
  }

  const filePath = getScalarFromFields(src, 'filePath', 'file_path')
  if (filePath != null && String(filePath).trim() !== '') {
    draft.filePath = toTypedField(String(filePath).trim(), 'String')
  }

  for (const key of PASS_THROUGH_TABLE_KEYS) {
    if (key === 'signature_file_list') continue
    const field = src[key]
    if (tableRowCount(field) > 0 && isTypedField(field)) {
      draft[key] = deepClone(field)
    }
  }

  for (const attr of FILE_ATTACHMENT_ATTRIBUTE_KEYS) {
    const field = src[attr]
    if (isTruthyOutgoingAttribute(field)) {
      draft[attr] = isTypedField(field) ? deepClone(field) : toTypedField(true, 'Boolean')
    }
  }

  if (options?.selected) {
    draft.__selected = toTypedField(true, 'Boolean')
  }

  for (const key of PASS_THROUGH_SCALAR_KEYS) {
    const field = src[key]
    if (field == null) continue
    const plain = unwrapFieldValue(field)
    if (plain === undefined || plain === null || plain === '') continue
    if (key === 'stampable' && (plain === 0 || plain === '0' || plain === false)) continue
    draft[key] = isTypedField(field) ? deepClone(field) : toTypedField(plain)
  }

  applyOutgoingDirectionsFields(draft, src)

  const clientRowKey = getAttachmentClientRowKey(src)
  if (clientRowKey) {
    draft[ATTACHMENT_CLIENT_ROW_KEY] = toTypedField(clientRowKey, 'String')
  }

  // WUI-305: staging после DS — иначе теряется при fileItemsToUniDataTable → store
  const stagingUri = getScalarFromFields(src, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS)
  if (stagingUri != null && String(stagingUri).trim() !== '') {
    draft[IKey.STORAGE_URI] = toTypedField(String(stagingUri).trim(), 'String')
  }

  return orderSwingOutgoingRowFields(draft)
}

function isTruthyOutgoingAttribute(field: unknown): boolean {
  if (field == null) return false
  const plain = unwrapFieldValue(field)
  return plain === true || plain === 'true' || plain === '1' || plain === 1
}

/** Inline getData(actionId): только `code` и `version` выбранной строки (Swing). */
export function buildInlineAttachmentElementUniData(item: FileItem | null): UniData {
  const ud = new UniData()
  if (!item) return ud
  const src = (item.fields ?? {}) as Record<string, unknown>
  const code = resolveCode(src)
  if (code == null) return ud
  ud.add({
    code: toTypedField(code, 'Long'),
    version: toTypedField(resolveVersion(src), 'Long'),
  })
  return ud
}

/**
 * `FileItem[]` → типизированное поле формы / ответ getData / putFullData.
 *
 * Wire-shape совпадает с Swing UniDataTable: даже для пустой ленты нужны
 * `attributes` / `columns` / `rows` / `empty`, иначе middleware/скрипты
 * получают «дыры» в currentData вместо пустой таблицы.
 */
export function fileItemsToUniDataTable(
  items: FileItem[],
  options?: { selectedItem?: FileItem | null },
): UniDataTableWire {
  const selected = options?.selectedItem ?? null
  const rows = items.map((item) => ({
    fields: buildOutgoingRowFields(item, {
      selected: selected != null && attachmentItemsMatch(item, selected),
    }),
    errors: {},
    attributes: {},
  }))

  return {
    type: 'UniDataTable',
    value: {
      attributes: {},
      columns: [],
      rows,
      empty: rows.length === 0,
    },
  }
}

/**
 * Каноническая пустая UniDataTable для FILEATTACHMENTS в currentData.
 * Используется, когда лента пуста или элемент скрыт/не смонтирован.
 */
export function emptyFileAttachmentsUniDataTable(): UniDataTableWire {
  return fileItemsToUniDataTable([])
}

/** `currentData` для `POST /platform/upload` (п. 7 WUI-293): `name` = имя файла, не поле формы. */
export function buildAttachmentUploadCurrentData(options: {
  file: File
  elementName: string
  existingFields?: Record<string, unknown>
}): Record<string, TypedPlatformField> {
  const src = options.existingFields ?? {}
  const out: Record<string, TypedPlatformField> = {
    name: toTypedField(options.file.name, 'String'),
    size: toTypedField(options.file.size, 'Long'),
    [IKey.COMPLEX_ELEMENT_NAME]: toTypedField(options.elementName, 'String'),
  }

  const code = getScalarFromFields(src, 'code', '_id')
  if (code != null && code !== '' && Number(code) !== 0) {
    out.code = toTypedField(code, 'Long')
    out.version = toTypedField(getScalarFromFields(src, 'version', '_versionId') ?? 0, 'Long')
  } else {
    // Новый файл — как SwingAttachments.getFullData / sendFileData (code/version = -1).
    out.code = toTypedField(-1, 'Long')
    out.version = toTypedField(-1, 'Long')
  }

  const hash = getScalarFromFields(src, 'hash')
  if (hash != null && hash !== '') {
    out.hash = toTypedField(String(hash), 'String')
  }

  const pages = getScalarFromFields(src, 'pages')
  if (pages != null && pages !== '') {
    out.pages = toTypedField(String(pages), 'String')
  }

  return out
}

/** JSON `request` для `POST /platform/upload` (без лишнего `clientData` внутри `currentData`). */
export function buildAttachmentUploadRequest(params: {
  elementId: number
  session: { sessionId: string; isClone: boolean; cloneOf: string | null; type?: string }
  file: File
  elementName: string
  existingFields?: Record<string, unknown>
}): Record<string, unknown> {
  const fieldMap = buildAttachmentUploadCurrentData({
    file: params.file,
    elementName: params.elementName,
    existingFields: params.existingFields,
  })

  const request: Record<string, unknown> = {
    elementId: params.elementId,
    sessionId: params.session.sessionId,
    currentData: {
      fields: {
        fields: fieldMap,
        fieldAttributes: {},
        errors: {},
        attributes: {},
      },
      formError: null,
      exception: null,
    },
  }

  if (params.session.isClone) {
    request.isClone = true
    if (params.session.cloneOf) {
      request.cloneOf = params.session.cloneOf
    }
  }
  if (params.session.type) {
    request.type = params.session.type
  }

  return request
}

/**
 * Re-open prepare по staging: без code=-1/version=-1 — иначе middleware читает платформу
 * (`file -1 not found`) вместо copy-on-prepare из temp.
 */
export function sanitizePrepareFieldMapForStagingReuse(
  fieldMap: Record<string, TypedPlatformField>,
): void {
  const storageUri = getScalarFromFields(fieldMap, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS)
  if (storageUri == null || String(storageUri).trim() === '') return

  const codeRaw = getScalarFromFields(fieldMap, 'code', '_id')
  const codeNum = Number(codeRaw)
  if (!Number.isFinite(codeNum) || codeNum <= 0) {
    delete fieldMap.code
    delete fieldMap.version
  }
}

/** Поля currentData для prepare: filePath + hash; IKey.STORAGE_URI опционально для reuse temp из middleware. */
export function buildAttachmentPrepareCurrentData(options: {
  file: File
  elementName: string
  existingFields?: Record<string, unknown>
}): Record<string, TypedPlatformField> {
  const src = options.existingFields ?? {}
  const fieldMap = buildAttachmentUploadCurrentData(options)
  const filePath = getScalarFromFields(src, 'filePath', 'file_path', '__filePath')
  if (filePath != null && String(filePath).trim() !== '') {
    fieldMap.filePath = toTypedField(String(filePath).trim(), 'String')
  }
  const storageUri = getScalarFromFields(src, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS)
  if (storageUri != null && String(storageUri).trim() !== '') {
    fieldMap[IKey.STORAGE_URI] = toTypedField(String(storageUri).trim(), 'String')
  }
  sanitizePrepareFieldMapForStagingReuse(fieldMap)
  return fieldMap
}

/** JSON part `request` для `POST /platform/office-editor/prepare` (WUI-305). */
export function buildAttachmentPrepareRequest(params: {
  elementId: number
  session: { sessionId: string; isClone: boolean; cloneOf: string | null; type?: string }
  elementName: string
  existingFields?: Record<string, unknown>
  /** Режим открытия редактора (для browser JWT payload на middleware). */
  mode?: 'edit' | 'view'
  socketId?: string | null
  /** Локальный File для нового вложения (multipart part `file`). */
  file?: File | null
}): Record<string, unknown> {
  const src = params.existingFields ?? {}
  const fileNameFromFields =
    String(getScalarFromFields(src, 'name', 'fileName') ?? '').trim() || 'document.docx'
  const file =
    params.file instanceof File
      ? params.file
      : new File([], fileNameFromFields)
  const fieldMap = buildAttachmentPrepareCurrentData({
    file,
    elementName: params.elementName,
    existingFields: params.existingFields,
  })

  const documentSessionId = String(
    getScalarFromFields(src, ATTACHMENT_DOCUMENT_SESSION_ID, 'documentSessionId') ?? '',
  ).trim()
  if (documentSessionId) {
    setMiddlewareDocumentSessionIdField(fieldMap, documentSessionId)
  }
  fieldMap.r7Mode = toTypedField(params.mode === 'view' ? 'view' : 'edit', 'String')

  // Core слота уже открыт: isClone=true заставит MW клонировать новый core
  const request: Record<string, unknown> = {
    elementId: params.elementId,
    sessionId: params.session.sessionId,
    socketId: params.socketId ?? null,
    currentData: {
      fields: {
        fields: fieldMap,
        fieldAttributes: {},
        errors: {},
        attributes: {},
      },
      formError: null,
      exception: null,
    },
  }

  if (params.session.type) {
    request.type = params.session.type
  }

  return request
}

/** `request` + multipart `file` для prepare из строки FILEATTACHMENTS (WUI-305). */
export function buildAttachmentPrepareRequestFromItem(params: {
  elementId: number
  elementName: string
  session: { sessionId: string; isClone: boolean; cloneOf: string | null; type?: string }
  item: FileItem
  mode?: 'edit' | 'view'
  socketId?: string | null
}): { request: Record<string, unknown>; prepareFile: File | undefined } {
  const prepareFile = resolveAttachmentPrepareLocalFile(params.item)
  return {
    prepareFile,
    request: buildAttachmentPrepareRequest({
      elementId: params.elementId,
      session: params.session,
      elementName: params.elementName,
      existingFields: params.item.fields as Record<string, unknown>,
      mode: params.mode,
      socketId: params.socketId ?? null,
      file: prepareFile,
    }),
  }
}

/** Снимок полей строки для отладки platform read / prepare (WUI-305). */
export type AttachmentRowPlatformSnapshot = {
  attachmentKey: string
  code: unknown
  version: unknown
  hash: unknown
  filePath: unknown
  storageUri: unknown
  name: unknown
}

export function snapshotAttachmentRowFields(
  fields: Record<string, unknown>,
): AttachmentRowPlatformSnapshot {
  return {
    attachmentKey: rowAttachmentKey(fields),
    code: getScalarFromFields(fields, 'code', '_id', 'CODE', 'Code'),
    version: getScalarFromFields(fields, 'version', '_versionId'),
    hash: getScalarFromFields(fields, 'hash'),
    filePath: getScalarFromFields(fields, 'filePath', 'file_path', '__filePath'),
    storageUri: getScalarFromFields(fields, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS),
    name: getScalarFromFields(fields, 'name', 'fileName'),
  }
}

/** Поля multipart `request` для `POST /platform/office-editor/prepare` (DEV-лог). */
export function snapshotAttachmentPrepareRequest(request: Record<string, unknown>): AttachmentRowPlatformSnapshot & {
  elementId: unknown
  sessionId: unknown
} {
  const currentData = request.currentData as
    | { fields?: { fields?: Record<string, unknown> } }
    | undefined
  const rowFields = currentData?.fields?.fields ?? {}
  return {
    ...snapshotAttachmentRowFields(rowFields),
    elementId: request.elementId ?? null,
    sessionId: request.sessionId ?? null,
  }
}

function prepareResponseFieldMap(body: unknown): Record<string, unknown> | null {
  const unwrapped = unwrapPlatformJsonPayload(body)
  if (unwrapped == null || typeof unwrapped !== 'object') return null
  const root = unwrapped as Record<string, unknown>
  const candidates = [
    root,
    root.fields,
    (root.fields as Record<string, unknown> | undefined)?.fields,
    root.data,
    (root.data as Record<string, unknown> | undefined)?.fields,
  ]
  for (const candidate of candidates) {
    if (candidate == null || typeof candidate !== 'object' || Array.isArray(candidate)) continue
    const map = candidate as Record<string, unknown>
    if (
      'documentUrl' in map
      || 'documentKey' in map
      || IKey.STORAGE_URI in map
    ) {
      return map
    }
  }
  return null
}

/** Разбор `docEditorInitConfig` из ответа prepare (JSON-строка или объект). */
export function parseDocEditorInitConfig(raw: unknown): R7DocEditorInitConfig | null {
  const unwrapped = unwrapPlatformJsonPayload(raw)
  if (unwrapped == null || typeof unwrapped !== 'object' || Array.isArray(unwrapped)) return null
  const candidate = unwrapped as Record<string, unknown>
  const document = candidate.document
  const editorConfig = candidate.editorConfig
  const documentType = candidate.documentType
  if (!document || typeof document !== 'object' || Array.isArray(document)) return null
  if (!editorConfig || typeof editorConfig !== 'object' || Array.isArray(editorConfig)) return null
  const doc = document as Record<string, unknown>
  const ec = editorConfig as Record<string, unknown>
  const key = String(doc.key ?? '').trim()
  const url = String(doc.url ?? '').trim()
  const fileType = String(doc.fileType ?? '').trim()
  const title = String(doc.title ?? '').trim()
  const permissions = doc.permissions
  const user = ec.user
  const mode = ec.mode
  if (!key || !url || !fileType || !title) return null
  if (!permissions || typeof permissions !== 'object' || Array.isArray(permissions)) return null
  if (!user || typeof user !== 'object' || Array.isArray(user)) return null
  if (mode !== 'edit' && mode !== 'view') return null
  const userObj = user as Record<string, unknown>
  const userId = String(userObj.id ?? '').trim()
  const userName = String(userObj.name ?? '').trim()
  if (!userId || !userName) return null
  const docType = String(documentType ?? '').trim()
  if (!['word', 'cell', 'slide', 'pdf'].includes(docType)) return null
  return {
    document: {
      fileType,
      key,
      title,
      url,
      permissions: permissions as Record<string, boolean>,
    },
    documentType: docType as R7DocEditorInitConfig['documentType'],
    editorConfig: {
      mode,
      lang: typeof ec.lang === 'string' ? ec.lang : undefined,
      customization:
        ec.customization && typeof ec.customization === 'object' && !Array.isArray(ec.customization)
          ? (ec.customization as { forcesave?: boolean })
          : undefined,
      user: { id: userId, name: userName },
      callbackUrl: typeof ec.callbackUrl === 'string' ? ec.callbackUrl : undefined,
    },
  }
}

/** Разбор ответа `POST /platform/office-editor/prepare`. */
export function parseOfficeEditorPrepareResponse(body: unknown): {
  documentKey: string
  documentUrl: string
  callbackUrl: string
  storageUri: string
  documentSessionId: string
  generation: number
  dsDirty: boolean
  docEditorInitConfig: R7DocEditorInitConfig
  token?: string
} | null {
  const fields = prepareResponseFieldMap(body)
  if (!fields) return null
  const documentKey = String(getScalarFromFields(fields, 'documentKey') ?? '').trim()
  const documentUrl = String(getScalarFromFields(fields, 'documentUrl') ?? '').trim()
  const callbackUrl = String(getScalarFromFields(fields, 'callbackUrl') ?? '').trim()
  const storageUri = String(
    getScalarFromFields(fields, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS, 'STORAGE_URI') ?? '',
  ).trim()
  const documentSessionId = String(
    getScalarFromFields(fields, ATTACHMENT_DOCUMENT_SESSION_ID, 'documentSessionId') ?? '',
  ).trim()
  if (!documentKey || !documentUrl || !callbackUrl || !storageUri || !documentSessionId) return null
  const generationRaw = Number(getScalarFromFields(fields, 'generation'))
  const generation = Number.isFinite(generationRaw) ? generationRaw : 0
  const dsDirtyRaw = getScalarFromFields(fields, 'dsDirty', 'ds_dirty')
  const dsDirty =
    dsDirtyRaw === true
    || dsDirtyRaw === 'true'
    || dsDirtyRaw === 1
    || dsDirtyRaw === '1'
  const token = String(getScalarFromFields(fields, 'token') ?? '').trim() || undefined
  const docEditorInitConfig = parseDocEditorInitConfig(
    getScalarFromFields(fields, 'docEditorInitConfig'),
  )
  if (!docEditorInitConfig) return null
  const result: {
    documentKey: string
    documentUrl: string
    callbackUrl: string
    storageUri: string
    documentSessionId: string
    generation: number
    dsDirty: boolean
    docEditorInitConfig: R7DocEditorInitConfig
    token?: string
  } = {
    documentKey,
    documentUrl,
    callbackUrl,
    storageUri,
    documentSessionId,
    generation,
    dsDirty,
    docEditorInitConfig,
  }
  if (token) {
    result.token = token
  }
  return result
}

/** Meta staging из ответа prepare (тот же shape, что первый platform download). */
export function officeStagingMetaFromPrepareResponse(prepared: {
  documentKey: string
  documentUrl: string
  callbackUrl: string
  storageUri: string
  documentSessionId: string
  generation: number
  dsDirty: boolean
}): PlatformDownloadMeta {
  return {
    ...prepared,
    fullPrepareBundle: true,
  }
}

/** Опциональные query-поля platform read из row (code/version/fileName/hash/filePath). */
export function appendAttachmentPlatformReadQueryFields(
  params: Record<string, string | number>,
  fields: Record<string, unknown>,
): void {
  const fileName = String(getScalarFromFields(fields, 'fileName', 'name') ?? '').trim()
  if (fileName) params.fileName = fileName

  const code = getScalarFromFields(fields, 'code', '_id', 'CODE', 'Code')
  if (code == null || code === '') return

  params.code = Number(code)
  const version = getScalarFromFields(fields, 'version', '_versionId') ?? 0
  params.version = Number(version)

  const hash = getScalarFromFields(fields, 'hash')
  if (hash != null && String(hash).trim() !== '') {
    params.hash = String(hash).trim()
  }

  const filePath = getScalarFromFields(fields, 'filePath', 'file_path', '__filePath')
  if (filePath != null && String(filePath).trim() !== '') {
    params.filePath = String(filePath).trim()
  }
}

function mergeOfficeStagingFieldsIntoRow(
  mergedFields: Record<string, unknown>,
  meta: PlatformDownloadMeta,
): void {
  const sessionField = toTypedField(meta.documentSessionId, 'String')
  mergedFields[ATTACHMENT_DOCUMENT_SESSION_ID] = sessionField
  mergedFields[MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD] = deepClone(sessionField)
  mergedFields.documentSessionId = deepClone(sessionField)

  if (meta.storageUri) {
    mergedFields[IKey.STORAGE_URI] = toTypedField(meta.storageUri, 'String')
  }

  if (!meta.fullPrepareBundle) return

  if (meta.documentKey) {
    mergedFields.documentKey = toTypedField(meta.documentKey, 'String')
  }
  if (meta.documentUrl) {
    mergedFields.documentUrl = toTypedField(meta.documentUrl, 'String')
  }
  if (meta.callbackUrl) {
    mergedFields.callbackUrl = toTypedField(meta.callbackUrl, 'String')
  }
}

function downloadMetaFieldMap(body: unknown): Record<string, unknown> | null {
  const fromPrepare = prepareResponseFieldMap(body)
  if (fromPrepare) return fromPrepare

  const unwrapped = unwrapPlatformJsonPayload(body)
  if (unwrapped == null || typeof unwrapped !== 'object') return null
  const root = unwrapped as Record<string, unknown>
  const candidates = [
    root,
    root.fields,
    (root.fields as Record<string, unknown> | undefined)?.fields,
    root.data,
    (root.data as Record<string, unknown> | undefined)?.fields,
  ]
  for (const candidate of candidates) {
    if (candidate == null || typeof candidate !== 'object' || Array.isArray(candidate)) continue
    const map = candidate as Record<string, unknown>
    if (
      MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD in map
      || ATTACHMENT_DOCUMENT_SESSION_ID in map
      || 'documentSessionId' in map
    ) {
      return map
    }
  }
  return null
}

/** Meta из part 1 `GET /platform/download` (полный prepare или cache-hit). */
export type PlatformDownloadMeta = {
  documentSessionId: string
  storageUri?: string
  documentKey?: string
  documentUrl?: string
  callbackUrl?: string
  generation: number
  dsDirty: boolean
  /** Полный DS-bundle как после prepare (первый platform read). */
  fullPrepareBundle: boolean
  /** Есть при `fullPrepareBundle` (ответ как после prepare). */
  docEditorInitConfig?: R7DocEditorInitConfig
  token?: string
}

export function getAttachmentDocumentSessionIdFromFields(
  fields: Record<string, unknown>,
): string | null {
  const id = String(
    getScalarFromFields(fields, ATTACHMENT_DOCUMENT_SESSION_ID, MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD, 'documentSessionId')
      ?? '',
  ).trim()
  return id || null
}

export function parsePlatformDownloadMeta(body: unknown): PlatformDownloadMeta | null {
  const fields = downloadMetaFieldMap(body)
  if (!fields) return null

  const documentSessionId = getAttachmentDocumentSessionIdFromFields(fields)
  if (!documentSessionId) return null

  const full = parseOfficeEditorPrepareResponse(body)
  if (full) {
    return {
      ...full,
      fullPrepareBundle: true,
    }
  }

  const storageUri = String(
    getScalarFromFields(fields, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS, 'STORAGE_URI') ?? '',
  ).trim()

  return {
    documentSessionId,
    storageUri: storageUri || undefined,
    generation: 0,
    dsDirty: false,
    fullPrepareBundle: false,
  }
}

/** Патч pageFields для FILEDOWNLOAD после platform download meta. */
export function buildPlatformDownloadPageFieldPatch(
  fieldName: string,
  meta: PlatformDownloadMeta,
): Record<string, { type: string; value: unknown }> {
  const patch: Record<string, { type: string; value: unknown }> = {}
  const sessionField = toTypedField(meta.documentSessionId, 'String')
  patch[`${fieldName}__documentSessionId`] = sessionField
  if (meta.storageUri) {
    patch[`${fieldName}__storage_uri`] = toTypedField(meta.storageUri, 'String')
  }
  if (meta.fullPrepareBundle) {
    if (meta.documentKey) {
      patch[`${fieldName}__documentKey`] = toTypedField(meta.documentKey, 'String')
    }
    if (meta.documentUrl) {
      patch[`${fieldName}__documentUrl`] = toTypedField(meta.documentUrl, 'String')
    }
    if (meta.callbackUrl) {
      patch[`${fieldName}__callbackUrl`] = toTypedField(meta.callbackUrl, 'String')
    }
  }
  return patch
}

/** Сохранить meta download/prepare в row (documentSessionId, storage_uri, полный DS-bundle). */
export function mergePlatformDownloadMetaIntoRow(
  item: FileItem,
  meta: PlatformDownloadMeta,
): FileItem {
  const mergedFields = deepClone(item.fields ?? {}) as Record<string, unknown>
  mergeOfficeStagingFieldsIntoRow(mergedFields, meta)

  return {
    ...item,
    fields: mergedFields as FileItem['fields'],
  }
}

/** Сравнение полей staging после `GET /platform/download` — skip store, если merge no-op. */
export function attachmentRowDownloadMetaEqual(a: FileItem, b: FileItem): boolean {
  const fieldsA = (a.fields ?? {}) as Record<string, unknown>
  const fieldsB = (b.fields ?? {}) as Record<string, unknown>
  const keys = [
    ATTACHMENT_DOCUMENT_SESSION_ID,
    MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD,
    'documentSessionId',
    IKey.STORAGE_URI,
    'storageUri',
    'documentKey',
    'documentUrl',
    'callbackUrl',
  ] as const
  for (const key of keys) {
    const va = getScalarFromFields(fieldsA, key)
    const vb = getScalarFromFields(fieldsB, key)
    if (String(va ?? '') !== String(vb ?? '')) return false
  }
  return true
}

/** Сброс documentSessionId перед retry platform read (протухшая сессия middleware). */
export function clearAttachmentDocumentSessionIdFields(item: FileItem): FileItem {
  const mergedFields = deepClone(item.fields ?? {}) as Record<string, unknown>
  delete mergedFields[ATTACHMENT_DOCUMENT_SESSION_ID]
  delete mergedFields[MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD]
  delete mergedFields.documentSessionId
  return {
    ...item,
    fields: mergedFields as FileItem['fields'],
  }
}

/** JSON `request` для promote DS-staging → платформа на getFullData (WUI-305). Требует `storage_uri` в строке. */
export function buildAttachmentR7PromoteUploadRequest(params: {
  elementId: number
  session: { sessionId: string; isClone: boolean; cloneOf: string | null; type?: string }
  elementName: string
  existingFields?: Record<string, unknown>
}): Record<string, unknown> {
  const src = params.existingFields ?? {}
  const fileName =
    String(getScalarFromFields(src, 'name', 'fileName') ?? '').trim() || 'document.docx'
  const fieldMap = buildAttachmentUploadCurrentData({
    file: new File([], fileName),
    elementName: params.elementName,
    existingFields: params.existingFields,
  })
  const storageUri = getScalarFromFields(src, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS)
  if (storageUri != null && String(storageUri).trim() !== '') {
    fieldMap[IKey.STORAGE_URI] = toTypedField(String(storageUri).trim(), 'String')
  }
  const documentSessionId = getScalarFromFields(
    src,
    ATTACHMENT_DOCUMENT_SESSION_ID,
    'documentSessionId',
  )
  if (documentSessionId != null && String(documentSessionId).trim() !== '') {
    setMiddlewareDocumentSessionIdField(fieldMap, String(documentSessionId))
  }

  const request: Record<string, unknown> = {
    elementId: params.elementId,
    sessionId: params.session.sessionId,
    currentData: {
      fields: {
        fields: fieldMap,
        fieldAttributes: {},
        errors: {},
        attributes: {},
      },
      formError: null,
      exception: null,
    },
  }

  if (params.session.isClone) {
    request.isClone = true
    if (params.session.cloneOf) {
      request.cloneOf = params.session.cloneOf
    }
  }
  if (params.session.type) {
    request.type = params.session.type
  }

  return request
}

/**
 * JSON-тело `POST document-sync-state`, `POST discard` и `POST forcesave`.
 * Lookup: только `documentSessionId` (R1, R11).
 */
export function buildAttachmentOfficeEditorDocumentRequest(params: {
  elementId: number
  session: { sessionId: string; isClone: boolean; cloneOf: string | null; type?: string }
  documentSessionId: string
}): Record<string, unknown> {
  const documentSessionId = String(params.documentSessionId ?? '').trim()
  const fieldMap: Record<string, TypedPlatformField> = {}
  setMiddlewareDocumentSessionIdField(fieldMap, documentSessionId)

  const request: Record<string, unknown> = {
    elementId: params.elementId,
    sessionId: params.session.sessionId,
    currentData: {
      fields: {
        fields: fieldMap,
        fieldAttributes: {},
        errors: {},
        attributes: {},
      },
      formError: null,
      exception: null,
    },
  }

  if (params.session.isClone) {
    request.isClone = true
    if (params.session.cloneOf) {
      request.cloneOf = params.session.cloneOf
    }
  }
  if (params.session.type) {
    request.type = params.session.type
  }

  return request
}

function documentSyncStateResponseFieldMap(body: unknown): Record<string, unknown> | null {
  const unwrapped = unwrapPlatformJsonPayload(body)
  if (unwrapped == null || typeof unwrapped !== 'object') return null
  const root = unwrapped as Record<string, unknown>
  const candidates = [
    root,
    root.fields,
    (root.fields as Record<string, unknown> | undefined)?.fields,
    root.data,
    (root.data as Record<string, unknown> | undefined)?.fields,
  ]
  for (const candidate of candidates) {
    if (candidate == null || typeof candidate !== 'object' || Array.isArray(candidate)) continue
    const map = candidate as Record<string, unknown>
    if (isMiddleErrorResponse(map)) continue
    if ('syncState' in map || 'sync_state' in map || 'state' in map || 'status' in map) {
      return map
    }
  }
  return null
}

/**
 * Разбор ответа `POST /platform/office-editor/document-sync-state`.
 * Поле `syncState`: `syncing` | `ready` | `stale` | `error`; при `ready` — `dsStatus` 2|6.
 */
export function parseOfficeEditorDocumentSyncStateResponse(body: unknown): {
  syncState: 'syncing' | 'ready' | 'stale' | 'error'
  dsStatus?: number
  updatedAt?: number
  message?: string
  documentSessionId?: string
  documentKey?: string
  storageUri?: string
  documentUrl?: string
  generation?: number
  dsDirty?: boolean
} | null {
  const fields = documentSyncStateResponseFieldMap(body)
  if (!fields) return null

  const raw =
    getScalarFromFields(fields, 'syncState', 'sync_state', 'state') ?? ''
  const syncStateRaw = String(raw).trim().toLowerCase()
  let syncState: 'syncing' | 'ready' | 'stale' | 'error' | null = null
  if (
    syncStateRaw === 'syncing'
    || syncStateRaw === 'ready'
    || syncStateRaw === 'stale'
    || syncStateRaw === 'error'
  ) {
    syncState = syncStateRaw
  }

  const statusInt = Number(getScalarFromFields(fields, 'status'))
  if (!syncState && Number.isFinite(statusInt)) {
    // legacy mapping (старый middleware / переходный период)
    if (statusInt === 1000) syncState = 'syncing'
    else if (statusInt === 2 || statusInt === 6) syncState = 'ready'
    else if (statusInt === 1001 || statusInt === 3 || statusInt === 7) syncState = 'stale'
  }

  if (!syncState) return null

  const dsStatusRaw = Number(getScalarFromFields(fields, 'dsStatus', 'ds_status'))
  const dsStatus =
    Number.isFinite(dsStatusRaw) && dsStatusRaw >= 1 && dsStatusRaw <= 7 ? dsStatusRaw : undefined

  const updatedAt = Number(getScalarFromFields(fields, 'updatedAt', 'updated_at'))
  const message = getScalarFromFields(fields, 'message')
  const documentSessionId =
    String(getScalarFromFields(fields, ATTACHMENT_DOCUMENT_SESSION_ID, 'documentSessionId') ?? '').trim()
    || undefined
  const documentKey = String(getScalarFromFields(fields, 'documentKey') ?? '').trim() || undefined
  const storageUri = String(
    getScalarFromFields(fields, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS, 'STORAGE_URI') ?? '',
  ).trim() || undefined
  const documentUrl = String(getScalarFromFields(fields, 'documentUrl') ?? '').trim() || undefined
  const generationRaw = Number(getScalarFromFields(fields, 'generation'))
  const generation = Number.isFinite(generationRaw) ? generationRaw : undefined
  const dsDirtyRaw = getScalarFromFields(fields, 'dsDirty', 'ds_dirty')
  const dsDirty =
    dsDirtyRaw === true
    || dsDirtyRaw === 'true'
    || dsDirtyRaw === 1
    || dsDirtyRaw === '1'
      ? true
      : dsDirtyRaw === false || dsDirtyRaw === 'false' || dsDirtyRaw === 0 || dsDirtyRaw === '0'
        ? false
        : undefined

  return {
    syncState,
    dsStatus: syncState === 'ready' ? dsStatus : undefined,
    updatedAt: Number.isFinite(updatedAt) && updatedAt > 0 ? updatedAt : undefined,
    message: message != null ? String(message) : undefined,
    documentSessionId,
    documentKey,
    storageUri,
    documentUrl,
    generation,
    dsDirty,
  }
}

/** В ответе upload поле `attachments` часто = code (Long), не таблица элемента. */
function isUploadAttachmentsCodeAlias(key: string, val: unknown): boolean {
  if (key !== 'attachments') return false
  const plain = unwrapFieldValue(val)
  if (typeof plain === 'number') return true
  if (typeof plain === 'string' && /^\d+$/.test(plain.trim())) return true
  const typed = val as TypedPlatformField | undefined
  return typed?.type === 'Long' && tableRowCount(val) === 0
}

function attachmentRowIdentityFromFields(fields: Record<string, unknown>): boolean {
  if (isMiddleErrorResponse(fields)) return false

  const code = getScalarFromFields(fields, 'code', '_id', 'CODE', 'Code')
  const version = getScalarFromFields(fields, 'version', '_versionId')
  const hash = getScalarFromFields(fields, 'hash')
  const name = getScalarFromFields(fields, 'name', 'fileName')
  const filePath = getScalarFromFields(fields, 'filePath', 'file_path')

  if (code != null && code !== '' && Number(code) !== 0) {
    const codeField = fields.code ?? fields._id ?? fields.CODE ?? fields.Code
    if (isTypedField(codeField)) return true
    if (version != null && version !== '') return true
    if (hash != null && hash !== '' && name != null && name !== '') return true
    if (filePath != null && String(filePath).trim() !== '') return true
    if (
      isUploadAttachmentsCodeAlias('attachments', fields.attachments)
      && Number(getScalarFromFields(fields, 'attachments') ?? 0) > 0
    ) {
      return true
    }
    return false
  }
  if (
    isUploadAttachmentsCodeAlias('attachments', fields.attachments)
    && Number(getScalarFromFields(fields, 'attachments') ?? 0) > 0
  ) {
    return true
  }
  return hash != null && hash !== '' && name != null && name !== ''
}

/** Нормализует ответ upload: `attachments` (Long) → `code`, если code отсутствует. */
function normalizeUploadRowFields(row: Record<string, unknown>): Record<string, unknown> {
  if (getScalarFromFields(row, 'code', '_id', 'CODE', 'Code') != null) {
    return row
  }
  if (!isUploadAttachmentsCodeAlias('attachments', row.attachments)) {
    return row
  }
  const aliasCode = getScalarFromFields(row, 'attachments')
  if (aliasCode == null || aliasCode === '' || Number(aliasCode) === 0) {
    return row
  }
  return {
    ...row,
    code: isTypedField(row.attachments) ? deepClone(row.attachments) : toTypedField(aliasCode, 'Long'),
  }
}

function rowFieldsFromUniDataTableNode(node: unknown): Record<string, unknown> | null {
  const table = unwrapFieldValue(node)
  if (!table || typeof table !== 'object') return null
  const rows = (table as { rows?: unknown[] }).rows
  if (!Array.isArray(rows) || rows.length === 0) return null
  const first = rows[0]
  if (first && typeof first === 'object' && 'fields' in first) {
    return (first as { fields: Record<string, unknown> }).fields
  }
  if (first && typeof first === 'object' && !Array.isArray(first)) {
    return first as Record<string, unknown>
  }
  return null
}

function tryParseRowFieldsCandidate(candidate: unknown): Record<string, unknown> | null {
  if (candidate == null || typeof candidate !== 'object' || Array.isArray(candidate)) return null
  const obj = candidate as Record<string, unknown>

  if (attachmentRowIdentityFromFields(obj)) {
    return obj
  }

  for (const tableKey of ['attachments', 'attachment', 'file', 'files']) {
    if (tableKey in obj) {
      const row = rowFieldsFromUniDataTableNode(obj[tableKey])
      if (row && attachmentRowIdentityFromFields(row)) return row
    }
  }

  const nested = obj.fields
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const inner = nested as Record<string, unknown>
    if (attachmentRowIdentityFromFields(inner)) return inner
    for (const tableKey of ['attachments', 'attachment', 'file', 'files']) {
      if (tableKey in inner) {
        const row = rowFieldsFromUniDataTableNode(inner[tableKey])
        if (row && attachmentRowIdentityFromFields(row)) return row
      }
    }
  }

  return null
}

/** Разбор тела ответа `POST /platform/upload` → поля строки для merge (п. 8). */
export function parseAttachmentUploadResponse(body: unknown): Record<string, unknown> | null {
  const unwrapped = unwrapPlatformJsonPayload(body)
  if (unwrapped == null) return null
  if (typeof unwrapped !== 'object') return null
  if (isMiddleErrorResponse(unwrapped)) return null

  const root = unwrapped as Record<string, unknown>
  const directCandidates = [
    root,
    root.data,
    root.result,
    root.fields,
    (root.fields as Record<string, unknown> | undefined)?.fields,
  ]

  for (const candidate of directCandidates) {
    const row = tryParseRowFieldsCandidate(candidate)
    if (row) return normalizeUploadRowFields(row)
  }

  const visited = new Set<object>()
  const queue: unknown[] = [...directCandidates.filter(Boolean)]
  while (queue.length) {
    const node = queue.shift()
    if (node == null || typeof node !== 'object' || visited.has(node)) continue
    visited.add(node)

    const row = tryParseRowFieldsCandidate(node)
    if (row) return normalizeUploadRowFields(row)

    const obj = node as Record<string, unknown>
    for (const value of Object.values(obj)) {
      if (value == null) continue
      const unwrapped = unwrapFieldValue(value)
      if (unwrapped != null && typeof unwrapped === 'object') {
        queue.push(unwrapped)
      }
      if (typeof value === 'object') {
        queue.push(value)
      }
    }
  }

  return null
}

/** Снимок элемента для `swingGetData` / WS: таблица + `complex__element__name`. */
export function buildAttachmentsElementUniData(
  elementName: string,
  items: FileItem[],
  options?: { selectedItem?: FileItem | null },
): Record<string, TypedPlatformField | UniDataTableWire> {
  return {
    [elementName]: fileItemsToUniDataTable(items, options),
    [IKey.COMPLEX_ELEMENT_NAME]: toTypedField(elementName, 'String'),
  }
}

/** Ключ строки для merge по `code`/`version` (WUI-293, фаза 4 / 7). */
export function rowAttachmentKey(fields: Record<string, unknown>): string {
  const code = getScalarFromFields(fields, 'code', '_id')
  const version = getScalarFromFields(fields, 'version', '_versionId') ?? 0
  if (code === undefined || code === null || code === '') {
    return ''
  }
  return `${code}:${version}`
}

/**
 * Стабильный ключ строки для UI (лента, thumb-cache, focus dedup).
 * Совпадает с `dsAttachmentRowKey`, когда есть `code` или `__clientRowKey`;
 * иначе — session / staging / native File (без мутации `item.fields`).
 */
export function attachmentUiRowKey(item: FileItem): string {
  const fields = (item.fields ?? {}) as Record<string, unknown>
  const code = getScalarFromFields(fields, 'code', '_id')
  const codeNum = Number(code)
  const platformKey = rowAttachmentKey(fields)
  if (platformKey && Number.isFinite(codeNum) && codeNum > 0) {
    return platformKey
  }

  const clientKey = getAttachmentClientRowKey(fields)
  if (clientKey) return `client:${clientKey}`

  const sessionId = getAttachmentDocumentSessionIdFromFields(fields)
  if (sessionId) return `session:${sessionId}`

  const storageUri = getScalarFromFields(fields, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS)
  if (storageUri != null && String(storageUri).trim() !== '') {
    const name = String(getScalarFromFields(fields, 'name', 'fileName') ?? '').trim()
    return `staging:${String(storageUri).trim()}:${name}`
  }

  if (item.file instanceof File) {
    const f = item.file
    return `local:${f.name}:${f.size}:${f.lastModified}`
  }

  const name = String(getScalarFromFields(fields, 'name', 'fileName') ?? '').trim() || 'unknown'
  return `name:${name}`
}

export function rowHasServerCode(fields: Record<string, unknown>): boolean {
  const code = getScalarFromFields(fields, 'code', '_id')
  const codeNum = Number(code)
  if (!Number.isFinite(codeNum) || codeNum <= 0) return false
  return rowAttachmentKey(fields) !== ''
}

/**
 * Найти строку в актуальном списке по identity (см. `attachmentItemsReferSameRow`).
 */
export function findMatchingAttachmentItem(
  items: FileItem[],
  needle: FileItem,
): FileItem | undefined {
  const needleFields = (needle.fields ?? {}) as Record<string, unknown>
  const needleCv = resolveAttachmentRowCodeVersion(needleFields)
  if (needleCv && needleCv.code > 0) {
    const exactKey = rowAttachmentKey(needleFields)
    if (exactKey) {
      const exact = items.find(
        (row) => rowAttachmentKey((row.fields ?? {}) as Record<string, unknown>) === exactKey,
      )
      if (exact) return exact
    }
    const sameCode = items.filter((row) => {
      const cv = resolveAttachmentRowCodeVersion((row.fields ?? {}) as Record<string, unknown>)
      return cv?.code === needleCv.code
    })
    if (sameCode.length > 0) {
      return sameCode.reduce((acc, row) => {
        if (!acc) return row
        const accCv = resolveAttachmentRowCodeVersion((acc.fields ?? {}) as Record<string, unknown>)
        const rowCv = resolveAttachmentRowCodeVersion((row.fields ?? {}) as Record<string, unknown>)
        return (rowCv?.version ?? -1) > (accCv?.version ?? -1) ? row : acc
      }, sameCode[0] as FileItem)
    }
  }

  return items.find((row) => attachmentItemsReferSameRow(row, needle))
}

/** code/version строки вложения (учитывает `_id`, alias `attachments` → code). */
export function resolveAttachmentRowCodeVersion(
  fields: Record<string, unknown>,
): { code: number; version: number } | null {
  let codeRaw = getScalarFromFields(fields, 'code', '_id', 'CODE', 'Code')
  if (codeRaw == null || codeRaw === '' || Number(codeRaw) === 0) {
    const attachmentsField = fields.attachments
    if (
      attachmentsField &&
      typeof attachmentsField === 'object' &&
      'type' in attachmentsField &&
      (attachmentsField as { type?: string }).type === 'Long'
    ) {
      codeRaw = getScalarFromFields(fields, 'attachments')
    }
  }
  const code = Number(codeRaw)
  if (!Number.isFinite(code) || code <= 0) return null
  const versionRaw = getScalarFromFields(fields, 'version', '_versionId', 'VERSION', 'Version')
  const version = Number(versionRaw)
  return { code, version: Number.isFinite(version) ? version : 0 }
}

/**
 * Одна и та же строка вложения (delete, tombstone, merge).
 *
 * Приоритет: серверный `code` → `__clientRowKey` → DS session / staging → legacy File metadata.
 */
export function attachmentItemsReferSameRow(a: FileItem, b: FileItem): boolean {
  if (a === b) return true

  const af = (a.fields ?? {}) as Record<string, unknown>
  const bf = (b.fields ?? {}) as Record<string, unknown>

  const aCv = resolveAttachmentRowCodeVersion(af)
  const bCv = resolveAttachmentRowCodeVersion(bf)
  if (aCv && bCv && aCv.code === bCv.code) {
    return true
  }

  const aClient = getAttachmentClientRowKey(af)
  const bClient = getAttachmentClientRowKey(bf)
  if (aClient && bClient) {
    return aClient === bClient
  }

  const aSession = getAttachmentDocumentSessionIdFromFields(af)
  const bSession = getAttachmentDocumentSessionIdFromFields(bf)
  if (aSession && bSession && aSession === bSession) {
    return true
  }

  const aUri = getScalarFromFields(af, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS)
  const bUri = getScalarFromFields(bf, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS)
  if (
    aUri != null
    && bUri != null
    && String(aUri).trim() !== ''
    && String(aUri).trim() === String(bUri).trim()
  ) {
    const aName = String(getScalarFromFields(af, 'name', 'fileName') ?? '').trim()
    const bName = String(getScalarFromFields(bf, 'name', 'fileName') ?? '').trim()
    return !aName || !bName || aName === bName
  }

  if (a.file instanceof File && b.file instanceof File) {
    if (a.file === b.file) return true
    if (aClient || bClient) return false
    return (
      a.file.name === b.file.name &&
      a.file.size === b.file.size &&
      a.file.lastModified === b.file.lastModified
    )
  }

  if (!aClient && !bClient && !(a.file instanceof File) && !(b.file instanceof File)) {
    const aName = String(getScalarFromFields(af, 'name', 'fileName') ?? '').trim()
    const bName = String(getScalarFromFields(bf, 'name', 'fileName') ?? '').trim()
    const aSize = getScalarFromFields(af, 'size', '__size')
    const bSize = getScalarFromFields(bf, 'size', '__size')
    if (aName && aName === bName && aSize != null && aSize === bSize) {
      return true
    }
  }

  return false
}

function attachmentRowsMatchForTempOverlay(a: FileItem, b: FileItem): boolean {
  if (attachmentItemsReferSameRow(a, b)) return true
  const af = (a.fields ?? {}) as Record<string, unknown>
  const bf = (b.fields ?? {}) as Record<string, unknown>
  const aName = String(getScalarFromFields(af, 'name', 'fileName') ?? '').trim()
  const bName = String(getScalarFromFields(bf, 'name', 'fileName') ?? '').trim()
  const aSize = getScalarFromFields(af, 'size', '__size')
  const bSize = getScalarFromFields(bf, 'size', '__size')
  return Boolean(aName && aName === bName && aSize != null && Number(aSize) === Number(bSize))
}

export type AttachmentTempFieldSnapshot = {
  storageUri?: string | null
  documentSessionId?: string | null
}

/**
 * Скопировать MW temp (`storage_uri` / `documentSessionId`) на строку ленты
 * из wire-таблицы page/currentData или DS-снимка (identity: clientKey или name+size).
 */
export function overlayAttachmentTempFieldsFromSources(
  item: FileItem,
  sources: {
    wireRows?: FileItem[]
    snapshot?: AttachmentTempFieldSnapshot | null
  },
): FileItem {
  const fields = (item.fields ?? {}) as Record<string, unknown>
  const haveUri = Boolean(getAttachmentStorageUri(item))
  const haveSession = Boolean(getAttachmentDocumentSessionIdFromFields(fields))
  if (haveUri && haveSession) return item

  const match = (sources.wireRows ?? []).find((row) => attachmentRowsMatchForTempOverlay(item, row))
  const matchFields = (match?.fields ?? {}) as Record<string, unknown>
  const uri =
    (!haveUri ? getAttachmentStorageUri(match ?? { fields: {} }) : null)
    || (!haveUri ? String(sources.snapshot?.storageUri ?? '').trim() || null : null)
  const session =
    (!haveSession ? getAttachmentDocumentSessionIdFromFields(matchFields) : null)
    || (!haveSession ? String(sources.snapshot?.documentSessionId ?? '').trim() || null : null)
  if (!uri && !session) return item

  const merged = { ...fields }
  if (uri && !haveUri) {
    merged[IKey.STORAGE_URI] = toTypedField(uri, 'String')
  }
  if (session && !haveSession) {
    const sessionField = toTypedField(session, 'String')
    merged[ATTACHMENT_DOCUMENT_SESSION_ID] = sessionField
    merged[MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD] = deepClone(sessionField)
    merged.documentSessionId = deepClone(sessionField)
  }
  return { ...item, fields: merged as FileItem['fields'] }
}

export function overlayAttachmentItemsTempFromSources(
  items: FileItem[],
  sources: {
    wireRows?: FileItem[]
    resolveSnapshot?: (item: FileItem) => AttachmentTempFieldSnapshot | null | undefined
  },
): FileItem[] {
  return items.map((item) =>
    overlayAttachmentTempFieldsFromSources(item, {
      wireRows: sources.wireRows,
      snapshot: sources.resolveSnapshot?.(item) ?? null,
    }),
  )
}

/** Новое вложение: локальный `File`, ещё без `code` на платформе (диалог, appendBytes). */
export function isNewLocalAttachmentItem(item: FileItem): boolean {
  if (!(item.file instanceof File)) return false
  return !rowHasServerCode((item.fields ?? {}) as Record<string, unknown>)
}

/**
 * Строка из `is_refill` до `append_bytes`: в store уже есть name/size, но ещё нет `file: File`.
 * Повторный append создаёт дубль — нужно привязать `File` к существующей строке.
 */
export function findRefilledLocalRowForNativeFile(
  items: FileItem[],
  file: File,
): FileItem | undefined {
  return items.find((row) => {
    if (row.file instanceof File) return false
    const fields = (row.fields ?? {}) as Record<string, unknown>
    if (rowHasServerCode(fields)) return false
    const name = String(getScalarFromFields(fields, 'name', 'fileName') ?? '').trim()
    const size = Number(getScalarFromFields(fields, 'size', '__size'))
    return name === file.name && Number.isFinite(size) && size === file.size
  })
}

/**
 * Новое локальное вложение для prepare: multipart part `file` только до первого staging.
 * После prepare/WS есть `storage_uri` или `documentSessionId` — part `file` не слать:
 * middleware при `file` создаёт новый UUID-сеанс из байтов браузера и затирает правки Р7.
 * Повторное открытие после save — idempotent prepare по `documentSessionId` (без File).
 */
export function resolveAttachmentPrepareLocalFile(item: FileItem): File | undefined {
  if (!isNewLocalAttachmentItem(item)) return undefined
  if (getAttachmentStorageUri(item)) return undefined
  if (getAttachmentDocumentSessionIdFromFields((item.fields ?? {}) as Record<string, unknown>)) {
    return undefined
  }
  return item.file instanceof File ? item.file : undefined
}

/** URI временного файла после callback DS (WUI-305). */
export function getAttachmentStorageUri(item: FileItem): string | null {
  const fields = (item.fields ?? {}) as Record<string, unknown>
  const scalar = getScalarFromFields(fields, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS)
  if (scalar == null) return null
  const t = String(scalar).trim()
  return t || null
}

/**
 * Строка изменена для upload: явный `changed: true`, staging DS (`storage_uri`) или новый локальный файл.
 * `changed` не хранится в UniData — после round-trip через store восстанавливается эвристикой.
 */
export function isAttachmentItemChanged(item: FileItem): boolean {
  if (item.changed === true) return true
  if (getAttachmentStorageUri(item)) return true
  return isNewLocalAttachmentItem(item)
}

export function withAttachmentItemChanged(item: FileItem, changed = true): FileItem {
  return { ...item, changed }
}

/** Есть пригодные байты в поле `preview` (fill или ответ `AttachmentsPreviewFetcher`). */
export function rowHasUsablePreview(fields: Record<string, unknown>): boolean {
  const bytes = previewBytesFromFields(fields)
  return Boolean(bytes?.length)
}

function mergeAttachmentRowFields(existing: FileItem, incoming: FileItem): FileItem {
  const mergedFields = deepClone(existing.fields ?? {}) as Record<string, unknown>
  const inc = (incoming.fields ?? {}) as Record<string, unknown>

  for (const [key, val] of Object.entries(inc)) {
    if (val === undefined || val === null) continue
    if (key === 'preview') {
      if (rowHasUsablePreview(inc)) {
        mergedFields[key] = isTypedField(val) ? deepClone(val) : toTypedField(val)
      }
      continue
    }
    const plain = unwrapFieldValue(val)
    if (plain === undefined || plain === null || plain === '') continue
    mergedFields[key] = isTypedField(val) ? deepClone(val) : toTypedField(val)
  }

  return {
    ...existing,
    fields: mergedFields as FileItem['fields'],
    file: existing.file,
  }
}

function attachmentItemHasNativeFile(item: FileItem): boolean {
  return item.file instanceof File
}

/**
 * После round-trip через `UniDataTable` теряется `file: File` — восстанавливаем по identity строки.
 * Используется при sync из store и в `reconcileAttachmentTapeFromStore`.
 */
export function mergePreservedNativeFiles(incoming: FileItem[], previous: FileItem[]): FileItem[] {
  const consumedPrev = new Set<FileItem>()
  return incoming.map((item) => {
    if (attachmentItemHasNativeFile(item)) return item

    const prev =
      findMatchingAttachmentItem(previous, item)
      ?? previous.find(
        (p) => !consumedPrev.has(p) && attachmentUiRowKey(p) === attachmentUiRowKey(item),
      )

    const itemFields = (item.fields ?? {}) as Record<string, unknown>
    if (!prev?.file) return item
    consumedPrev.add(prev)

    const merged: FileItem = { ...item, file: prev.file, changed: prev.changed ?? item.changed }
    if (isNewLocalAttachmentItem(merged) || prev.changed === true) {
      return withAttachmentItemChanged(merged)
    }
    return merged
  })
}

/**
 * Синхронизация ленты из store: сохраняем локальный порядок и строки, ещё не попавшие в store,
 * не поднимаем удалённые локально строки из устаревшего `modelValue`.
 */
export function reconcileAttachmentTapeFromStore(
  incoming: FileItem[],
  previous: FileItem[],
): FileItem[] {
  if (previous.length === 0) {
    return mergePreservedNativeFiles(incoming, previous)
  }

  const consumedIncoming = new Set<FileItem>()
  const result: FileItem[] = []

  for (const prev of previous) {
    const inc =
      findMatchingAttachmentItem(
        incoming.filter((item) => !consumedIncoming.has(item)),
        prev,
      )
      ?? incoming.find(
        (item) =>
          !consumedIncoming.has(item) && attachmentUiRowKey(item) === attachmentUiRowKey(prev),
      )
    if (inc) {
      consumedIncoming.add(inc)
      result.push(mergePreservedNativeFiles([inc], [prev])[0]!)
    } else {
      result.push(prev)
    }
  }

  for (const inc of incoming) {
    if (consumedIncoming.has(inc)) continue
    if (findMatchingAttachmentItem(previous, inc)) continue
    const code = Number(getScalarFromFields((inc.fields ?? {}) as Record<string, unknown>, 'code', '_id'))
    if (Number.isFinite(code) && code > 0) {
      result.push(inc)
    }
  }

  return result
}

/** Строка из store — та же, что удалённая локально (tombstone). */
export function attachmentIncomingMatchesTombstone(item: FileItem, tombstone: FileItem): boolean {
  if (attachmentItemsReferSameRow(item, tombstone)) return true

  const tFields = (tombstone.fields ?? {}) as Record<string, unknown>
  const iFields = (item.fields ?? {}) as Record<string, unknown>
  const tClient = getAttachmentClientRowKey(tFields)
  if (!tClient || getAttachmentClientRowKey(iFields)) return false

  const tName = String(getScalarFromFields(tFields, 'name', 'fileName') ?? '').trim()
  const iName = String(getScalarFromFields(iFields, 'name', 'fileName') ?? '').trim()
  const tSize = getScalarFromFields(tFields, 'size', '__size')
  const iSize = getScalarFromFields(iFields, 'size', '__size')
  return Boolean(tName) && tName === iName && tSize != null && tSize === iSize
}

export function attachmentItemMatchesTombstone(
  item: FileItem,
  tombstones: FileItem[],
): boolean {
  if (tombstones.length === 0) return false
  return tombstones.some((t) => attachmentIncomingMatchesTombstone(item, t))
}

/** Merge входящей таблицы в текущую по `code`/`version` (reloadElement / refill, п. 12). */
export function mergeAttachmentsUniDataTables(
  existing: FileItem[] | UniDataTableWire,
  incoming: unknown,
): UniDataTableWire {
  const existingItems = Array.isArray(existing) ? existing : uniDataTableToFileItems(existing)
  const incomingItems = uniDataTableToFileItems(incoming)
  const byKey = new Map<string, FileItem>()

  const put = (item: FileItem) => {
    const fields = (item.fields ?? {}) as Record<string, unknown>
    const code = Number(getScalarFromFields(fields, 'code', '_id'))
    const platformKey =
      Number.isFinite(code) && code > 0 ? rowAttachmentKey(fields) : ''
    const key = platformKey || attachmentUiRowKey(item)
    const prev = byKey.get(key)
    byKey.set(key, prev ? mergeAttachmentRowFields(prev, item) : item)
  }

  for (const item of existingItems) put(item)
  for (const item of incomingItems) put(item)

  const mergedItems = [...byKey.values()]
  // Тот же wire-shape, что у fileItemsToUniDataTable (в т.ч. пустая таблица).
  return {
    type: 'UniDataTable',
    value: {
      attributes: {},
      columns: [],
      rows: mergedItems.map((item) => ({
        fields: deepClone(item.fields ?? {}) as Record<string, TypedPlatformField>,
        errors: {},
        attributes: {},
      })),
      empty: mergedItems.length === 0,
    },
  }
}

/**
 * После успешного `POST /platform/upload`: сливает ответ ядра в локальную строку (п. 8).
 *
 * Записывает `code`/`version`, не затирает pass-through штампы/подписи; снимает `file`.
 * Поле `attachments` в ответе, если это alias кода (Long), игнорируется.
 */
export function mergeUploadedRowResponse(
  serverRow: Record<string, unknown>,
  localItem: FileItem,
): FileItem {
  const mergedFields = deepClone(localItem.fields ?? {}) as Record<string, unknown>

  const serverFields =
    serverRow.fields && typeof serverRow.fields === 'object'
      ? (serverRow.fields as Record<string, unknown>)
      : serverRow

  for (const [key, val] of Object.entries(serverFields)) {
    if (val === undefined || val === null) continue
    if (isUploadAttachmentsCodeAlias(key, val)) {
      if (!getScalarFromFields(mergedFields, 'code', '_id', 'CODE', 'Code')) {
        mergedFields.code = isTypedField(val) ? deepClone(val) : toTypedField(val, 'Long')
      }
      continue
    }
    if (key === 'filePath' || key.startsWith('filePath')) {
      mergedFields[key] = isTypedField(val) ? deepClone(val) : toTypedField(val)
      continue
    }
    if (PLATFORM_TAIL_PREFIXES.some((p) => key.startsWith(p) || key === p)) continue
    mergedFields[key] = isTypedField(val) ? deepClone(val) : toTypedField(val)
  }

  delete mergedFields[IKey.STORAGE_URI]
  delete mergedFields.storageUri
  delete mergedFields.__storage_uri
  delete mergedFields.__storageUri

  const { file: _removed, ...rest } = localItem
  return {
    ...rest,
    changed: false,
    fields: mergedFields as FileItem['fields'],
  }
}

/** Имя поля элемента формы из XML/JSON (`name._text` / `_attributes.name`). */
function readFormElementFieldName(element: Record<string, unknown>): string {
  const name = element.name
  if (typeof name === 'string') return name.trim()
  if (name && typeof name === 'object' && '_text' in (name as object)) {
    return String((name as { _text?: unknown })._text ?? '').trim()
  }
  const attrs = element._attributes as { name?: string; id?: string } | undefined
  return String(attrs?.name ?? attrs?.id ?? '').trim()
}

/**
 * Обход дерева layout формы (rows / main / header / …).
 * Нужен, чтобы найти FileAttachments даже на скрытых панелях (v-if → unmount).
 */
function walkFormDefinitionForFileAttachments(
  node: unknown,
  visit: (element: Record<string, unknown>) => void,
): void {
  if (node == null) return
  if (Array.isArray(node)) {
    node.forEach((item) => walkFormDefinitionForFileAttachments(item, visit))
    return
  }
  if (typeof node !== 'object') return

  const record = node as Record<string, unknown>
  const element = record.element
  if (element && typeof element === 'object') {
    visit(element as Record<string, unknown>)
  }

  for (const key of ['rows', 'main', 'header', 'footer', 'tr', 'td', 'panelRows']) {
    if (record[key] != null) {
      walkFormDefinitionForFileAttachments(record[key], visit)
    }
  }
}

/**
 * Имена полей FileAttachments из определения формы.
 *
 * Registry (`elements`) теряет FA при unmount скрытой панели (`Panel.vue` — `v-if`).
 * Определение формы (`pageData.form`) остаётся — по нему восстанавливаем имена полей
 * для putFormData / createClientData / putFullData.
 */
export function collectFileAttachmentFieldNamesFromForm(formRoot: unknown): string[] {
  if (!formRoot || typeof formRoot !== 'object') return []
  const names = new Set<string>()
  const visit = (element: Record<string, unknown>) => {
    const elType = String(normalizeFormElementType(element) ?? '').toUpperCase()
    if (elType !== 'FILEATTACHMENTS') return
    const fieldName = readFormElementFieldName(element)
    if (fieldName) names.add(fieldName)
  }

  const root = formRoot as Record<string, unknown>
  const panels = root.panels
  // `panels` — map id→panel; Object.values обходим явно (не как layout-ключ walk).
  if (panels && typeof panels === 'object' && !Array.isArray(panels)) {
    for (const panel of Object.values(panels as Record<string, unknown>)) {
      walkFormDefinitionForFileAttachments(panel, visit)
    }
  }

  walkFormDefinitionForFileAttachments(root.main, visit)
  walkFormDefinitionForFileAttachments(root.header, visit)
  walkFormDefinitionForFileAttachments(root.footer, visit)
  // Layout stubs / already-expanded trees without a dedicated panels map.
  if (!panels) {
    walkFormDefinitionForFileAttachments(root, visit)
  }

  return [...names]
}

/** true, если поле объявлено как FileAttachments в form XML/JSON. */
export function isFileAttachmentFieldInForm(formRoot: unknown, fieldName: string): boolean {
  const wanted = String(fieldName ?? '').trim()
  if (!wanted) return false
  return collectFileAttachmentFieldNamesFromForm(formRoot).includes(wanted)
}

/** Имена FILEATTACHMENTS: form XML + смонтированный registry + extra. */
export function collectFileAttachmentFieldNames(
  formRoot?: unknown,
  elementsRegistry?: Record<string, unknown> | null,
  extraFieldNames?: Iterable<string>,
): string[] {
  const fieldNames = new Set<string>(collectFileAttachmentFieldNamesFromForm(formRoot))

  if (elementsRegistry && typeof elementsRegistry === 'object') {
    for (const reg of Object.values(elementsRegistry)) {
      if (!reg || typeof reg !== 'object') continue
      const elType = String(
        (reg as { elementType?: unknown }).elementType ?? normalizeFormElementType(reg) ?? '',
      ).toUpperCase()
      if (elType !== 'FILEATTACHMENTS') continue

      const fieldName = String(
        (reg as { fieldName?: unknown; name?: unknown }).fieldName ??
          (reg as { name?: unknown }).name ??
          '',
      ).trim()
      if (fieldName) fieldNames.add(fieldName)
    }
  }

  if (extraFieldNames) {
    for (const name of extraFieldNames) {
      const trimmed = String(name ?? '').trim()
      if (trimmed) fieldNames.add(trimmed)
    }
  }

  return [...fieldNames]
}

/** В таблице FA есть строка с валидным server `code`. */
export function fileAttachmentsTableHasUploadedRow(field: unknown): boolean {
  return uniDataTableToFileItems(field).some((item) =>
    rowHasServerCode((item.fields ?? {}) as Record<string, unknown>),
  )
}

/**
 * После onClick: не затирать FA с uploaded `code` снимком store (`code`≤0).
 * Имена полей — из form/registry, не хардкод.
 */
export function preserveUploadedFileAttachmentsFields(
  targetFields: Record<string, unknown>,
  onClickFields: Record<string, unknown>,
  fieldNames: Iterable<string>,
): { fields: Record<string, unknown>; changed: boolean } {
  const out = { ...targetFields }
  let changed = false
  for (const name of fieldNames) {
    const trimmed = String(name ?? '').trim()
    if (!trimmed) continue
    const fromOnClick = onClickFields[trimmed]
    if (fromOnClick === undefined) continue
    if (!fileAttachmentsTableHasUploadedRow(fromOnClick)) continue
    if (fileAttachmentsTableHasUploadedRow(out[trimmed])) continue
    out[trimmed] = fromOnClick
    changed = true
  }
  return { fields: out, changed }
}

/**
 * FILEATTACHMENTS-поля из всех панелей forms registry (включая скрытые).
 * getForm обычно пропускает invisible panels — FA всё равно нужно сериализовать.
 */
export function collectFileAttachmentFieldsFromFormsPanels(
  formPanels: Record<string, Record<string, Record<string, unknown>> | unknown> | null | undefined,
  fieldNames: Iterable<string>,
  mergeFn: (
    ...sources: Array<Record<string, unknown> | null | undefined>
  ) => Record<string, unknown> = (...sources) => {
    const out: Record<string, unknown> = {}
    for (const src of sources) {
      if (!src || typeof src !== 'object') continue
      Object.assign(out, src)
    }
    return out
  },
): Record<string, unknown> {
  const wanted = new Set(
    [...fieldNames].map((n) => String(n ?? '').trim()).filter(Boolean),
  )
  if (!formPanels || typeof formPanels !== 'object' || wanted.size === 0) return {}

  let collected: Record<string, unknown> = {}
  for (const panelData of Object.values(formPanels)) {
    if (!panelData || typeof panelData !== 'object' || Array.isArray(panelData)) continue
    for (const elementData of Object.values(panelData as Record<string, unknown>)) {
      if (!elementData || typeof elementData !== 'object' || Array.isArray(elementData)) continue
      const slice: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(elementData as Record<string, unknown>)) {
        if (wanted.has(key) && value !== undefined) {
          slice[key] = value
        }
      }
      if (Object.keys(slice).length > 0) {
        collected = mergeFn(collected, slice)
      }
    }
  }
  return collected
}

/**
 * Выбор значения FA: приоритет таблице с rows, затем любой UniDataTable, иначе undefined.
 * Не подменяет реальные вложения пустой таблицей, если данные есть в одном из источников.
 */
export function pickPreferredFileAttachmentsField(
  ...candidates: unknown[]
): unknown | undefined {
  let emptyOrPresentTable: unknown | undefined
  for (const candidate of candidates) {
    if (candidate === undefined) continue
    if (isFieldUniDataTable(candidate)) {
      if (uniDataTableToFileItems(candidate).length > 0) return candidate
      if (emptyOrPresentTable === undefined) emptyOrPresentTable = candidate
      continue
    }
    // Нетипизированный/частичный снимок с rows — тоже считаем данными.
    if (uniDataTableToFileItems(candidate).length > 0) return candidate
  }
  return emptyOrPresentTable
}

/**
 * Гарантирует UniDataTable для одного FA-поля в карте clientData/currentData.
 * Ищет данные во всех переданных картах; пустая таблица — только если данных нет.
 */
function ensureFileAttachmentsFieldInClientData(
  clientDataFields: Record<string, unknown>,
  mergedFlatParentData: Record<string, unknown>,
  fieldName: string,
  additionalFieldMaps?: Array<Record<string, unknown> | null | undefined>,
): void {
  const candidates: unknown[] = [
    clientDataFields[fieldName],
    mergedFlatParentData[fieldName],
  ]
  if (additionalFieldMaps) {
    for (const map of additionalFieldMaps) {
      if (map && typeof map === 'object' && fieldName in map) {
        candidates.push(map[fieldName])
      }
    }
  }

  const raw = pickPreferredFileAttachmentsField(...candidates)
  if (raw !== undefined && (isFieldUniDataTable(raw) || uniDataTableToFileItems(raw).length > 0)) {
    clientDataFields[fieldName] = fileItemsToUniDataTable(uniDataTableToFileItems(raw))
  } else {
    // Данных нет (пусто / String / отсутствует) → пустая UniDataTable, не type String.
    clientDataFields[fieldName] = emptyFileAttachmentsUniDataTable()
  }
}

/**
 * clientData / putFormData: каждое поле FILEATTACHMENTS — всегда UniDataTable.
 *
 * Источники имён:
 * 1) registry смонтированных элементов;
 * 2) `formRoot` — FA на скрытых/размонтированных панелях;
 * 3) `extraFieldNames` — явный allowlist вызывающего кода.
 *
 * Источники значений (до empty fallback): clientData, parentData, `additionalFieldMaps`
 * (page store / forms всех панелей). Есть rows → возвращаем их; иначе empty UniDataTable.
 */
export function applyFileAttachmentsToClientDataFieldsFromRegistry(
  clientDataFields: Record<string, unknown>,
  mergedFlatParentData: Record<string, unknown>,
  elementsRegistry: Record<string, unknown> | null | undefined,
  options?: {
    formRoot?: unknown
    extraFieldNames?: Iterable<string>
    additionalFieldMaps?: Array<Record<string, unknown> | null | undefined>
  },
): void {
  const fieldNames = collectFileAttachmentFieldNames(
    options?.formRoot,
    elementsRegistry,
    options?.extraFieldNames,
  )

  for (const fieldName of fieldNames) {
    ensureFileAttachmentsFieldInClientData(
      clientDataFields,
      mergedFlatParentData,
      fieldName,
      options?.additionalFieldMaps,
    )
  }
}
